/**
 * Rebuy Cart cross-sell — remount on variant change, themed nav, empty-state hide.
 */

const LOAD_TIMEOUT = 10000;
const REMOUNT_RETRIES = 12;

function widgetEl(root) {
  return root.querySelector('.rebuy-cart-crossell__widget');
}

function productCount(root) {
  return root.querySelectorAll(
    '.rebuy-product-block, .splide__slide:not(.splide__slide--clone)'
  ).length;
}

function setVisible(root, visible) {
  root.hidden = !visible;
  root.classList.toggle('is-rebuy-empty', !visible);

  const block = root.closest('.product__info-block-item');
  if (!block) return;

  block.hidden = !visible;

  // Hide the divider that sits directly above this block when the widget is empty
  const prev = block.previousElementSibling;
  if (prev?.matches?.('[data-block-type="divider"]')) {
    prev.hidden = !visible;
    prev.classList.toggle('is-rebuy-empty-divider', !visible);
  }
}

function setLoading(root, loading) {
  const el = root.querySelector('[data-rebuy-cart-loading]');
  if (!el) return;

  el.hidden = !loading;
  el.setAttribute('aria-busy', loading ? 'true' : 'false');
  root.classList.toggle('is-rebuy-ready', !loading);
}

// Re-runs on every render: Vue patches the same anchors on refetch, restoring
// the full "Product - Color" title, so this cannot be latched per element.
// Colourway products carry color only in the title, not as a real variant
// option, so it's moved in front of whatever option Rebuy already shows
// (e.g. size) to match colour-as-variant products, which already read
// "Color · Option" natively.
function stripTitles(root) {
  root.querySelectorAll('.rebuy-product-title-link').forEach((link) => {
    const full = (link.textContent || '').trim();
    const variantEl = link.closest('.rebuy-product-info')?.querySelector('.rebuy-variant-title');

    // Vue reuses the same node across product swaps and skips writing to it
    // when the new option text matches the old one verbatim (e.g. two
    // different products both sized "Compact Throw"). So the option cache
    // is only refreshed when the DOM text differs from what we last wrote —
    // if it matches, Vue left it alone, meaning the cached option is still
    // the true one and re-reading the (already-combined) DOM text would
    // stack the previous product's color onto the new one.
    if (variantEl) {
      const current = variantEl.textContent.trim();
      if (current !== variantEl.dataset.rebuyCombined) variantEl.dataset.rebuyOption = current;
    }

    const dashIndex = full.indexOf('-');
    if (dashIndex === -1) return;

    const stripped = full.slice(0, dashIndex).trim();
    if (!stripped) return;

    const color = full.slice(dashIndex + 1).trim();

    link.textContent = stripped;
    const aria = link.getAttribute('aria-label');
    if (aria?.includes(full)) link.setAttribute('aria-label', aria.replace(full, stripped));

    if (color && variantEl) {
      const option = variantEl.dataset.rebuyOption || '';
      const combined = option ? `${color} · ${option}` : color;
      variantEl.textContent = combined;
      variantEl.dataset.rebuyCombined = combined;
    }
  });
}

function markReady(root) {
  if (root.dataset.rebuyFetching === 'true') return false;

  stripTitles(root);

  if (productCount(root) === 0) {
    const instance = getInstance(widgetEl(root)?.getAttribute('data-rebuy-id'));
    if (Array.isArray(instance?.data?.products) && instance.data.products.length === 0) {
      setLoading(root, false);
      setVisible(root, false);
      return true;
    }
    return false;
  }

  setVisible(root, true);
  setLoading(root, false);
  mountedWidget = widgetEl(root);
  return true;
}

function goOneSlide(root, direction) {
  const list = root.querySelector('.splide__list');
  const track = root.querySelector('.splide__track');
  const slide = root.querySelector('.splide__slide:not(.splide__slide--clone)');
  if (!list || !track || !slide) return;

  const margin =
    parseFloat(getComputedStyle(slide).marginInlineEnd) ||
    parseFloat(getComputedStyle(slide).marginRight) ||
    0;
  const step = slide.getBoundingClientRect().width + margin;
  if (!step) return;

  const transform = getComputedStyle(list).transform;
  let currentX = 0;
  if (transform && transform !== 'none') {
    try {
      currentX = new DOMMatrixReadOnly(transform).m41;
    } catch (_) {
      const parts = transform.match(/matrix\(([^)]+)\)/)?.[1]?.split(',');
      currentX = parts ? parseFloat(parts[4]) || 0 : 0;
    }
  }

  const minX = Math.min(0, track.clientWidth - list.scrollWidth);
  const nextX = Math.max(
    minX,
    Math.min(0, direction === 'next' ? currentX - step : currentX + step)
  );
  if (Math.abs(nextX - currentX) < 1) return;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  list.style.transition = reduceMotion ? 'none' : 'transform 400ms ease';
  list.style.transform = `translate3d(${nextX}px, 0, 0)`;
}

/* --- Rebuy mounting ---------------------------------------------------
 * Colourways are separate products, so switching one swaps <product-info> and
 * detaches the node Vue renders the widget into. Rebuy keeps one instance per
 * widget ID and will not mount a second, so the new stub would stay empty.
 * Instead, carry the rendered node across the swap and refetch its products.
 */

let mountedWidget = null;

function rebuyInstances(widgetId) {
  const widgets = window.Rebuy?.widgets;
  if (!Array.isArray(widgets)) return [];
  return widgets.filter((w) => String(w?.id) === String(widgetId));
}

/**
 * Look the instance up by widget ID only. Rebuy's `instance.element` is the
 * placeholder Vue replaced on mount, so it is detached and never matches the
 * node actually rendering the widget.
 */
function getInstance(widgetId) {
  const instances = rebuyInstances(widgetId).filter((i) => i?.data);
  return instances[instances.length - 1] || null;
}

function isHydrated(node) {
  return !!node && node.childElementCount > 0;
}

function fetchProducts(root, instance, productId) {
  const current = [].concat(instance.data.shopify_product_ids || []).map(String);
  if (current.length === 1 && current[0] === String(productId)) {
    markReady(root);
    return;
  }

  setLoading(root, true);
  root.dataset.rebuyFetching = 'true';
  instance.data.shopify_product_ids = [Number(productId)];

  let settled = false;
  const done = () => {
    if (settled) return;
    settled = true;
    delete root.dataset.rebuyFetching;
    markReady(root);
  };

  // Rebuy swallows failed requests without invoking the callback
  setTimeout(done, LOAD_TIMEOUT);

  if (instance.getWidgetProducts) instance.getWidgetProducts(done);
  else done();
}

/**
 * Move the rendered widget node into this block, replacing the server-rendered
 * stub. The section swap detaches the node Vue renders into, and Rebuy will not
 * mount a second instance for the same widget ID, so the stub stays empty.
 */
function adoptMountedWidget(root) {
  const stub = widgetEl(root);
  if (!stub || stub === mountedWidget || !isHydrated(mountedWidget)) return;

  const productId = stub.getAttribute('data-rebuy-shopify-product-ids');
  if (productId) mountedWidget.setAttribute('data-rebuy-shopify-product-ids', productId);

  stub.replaceWith(mountedWidget);
}

function hydrate(root, attempt = 0) {
  if (attempt > REMOUNT_RETRIES) return;
  if (!widgetEl(root)?.isConnected) return;

  const rebuy = window.Rebuy;
  if (!rebuy?.widgets) {
    setTimeout(() => hydrate(root, attempt + 1), 300);
    return;
  }

  adoptMountedWidget(root);

  const widget = widgetEl(root);
  const instance = getInstance(widget.getAttribute('data-rebuy-id'));
  const productId = widget.getAttribute('data-rebuy-shopify-product-ids');

  if (instance && productId) {
    if (isHydrated(widget)) mountedWidget = widget;
    fetchProducts(root, instance, productId);
    return;
  }

  // First mount on this page load — let Rebuy hydrate the node itself
  rebuy.init?.();

  setTimeout(() => {
    const current = widgetEl(root);
    if (!current?.isConnected) return;

    if (isHydrated(current)) {
      mountedWidget = current;
      return;
    }

    hydrate(root, attempt + 1);
  }, 500);
}

function bind(root) {
  if (!root || root.dataset.navBound === 'true') return;

  const prevBtn = root.querySelector('[data-rebuy-cart-prev]');
  const nextBtn = root.querySelector('[data-rebuy-cart-next]');
  const stage = root.querySelector('.rebuy-cart-crossell__stage');
  if (!prevBtn || !nextBtn || !stage || !widgetEl(root)) return;

  root.dataset.navBound = 'true';
  setVisible(root, true);

  prevBtn.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    goOneSlide(root, 'prev');
  });

  nextBtn.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    goOneSlide(root, 'next');
  });

  // Stage survives widget-node replacement on remount
  new MutationObserver(() => markReady(root)).observe(stage, {
    childList: true,
    subtree: true,
  });

  if (!markReady(root)) {
    setTimeout(() => {
      if (root.classList.contains('is-rebuy-ready')) return;
      setLoading(root, false);
      if (productCount(root) === 0) setVisible(root, false);
    }, LOAD_TIMEOUT);
  }

  hydrate(root);
}

function initAll() {
  document.querySelectorAll('[data-rebuy-cart-crossell]').forEach(bind);
}

initAll();

document.addEventListener('rebuy.ready', () => {
  initAll();
  document.querySelectorAll('[data-rebuy-cart-crossell]').forEach(markReady);
});

new MutationObserver((mutations) => {
  const added = mutations.some((m) =>
    [...m.addedNodes].some(
      (n) =>
        n.nodeType === 1 &&
        (n.matches?.('[data-rebuy-cart-crossell]') || n.querySelector?.('[data-rebuy-cart-crossell]'))
    )
  );
  if (added) initAll();
}).observe(document.body, { childList: true, subtree: true });

document.addEventListener('shopify:section:load', (event) => {
  event.target.querySelectorAll?.('[data-rebuy-cart-crossell]').forEach((root) => {
    delete root.dataset.navBound;
    bind(root);
  });
});

// Rebuy's own add-to-cart button bypasses window.Cart, so the cart page
// won't otherwise learn a line item was added.
document.addEventListener('rebuy:cart.change', () => {
  window.Cart?.refresh();
});
